import { useCallback, useEffect, useRef, useState } from 'react'

import { ApiError, getHistory, getLog } from '@/lib/api'
import { CommandKind, type ClosedTrade } from '@/lib/types'
import { useAppStore } from '@/store/useAppStore'
import {
  formatUsd,
  formatPrice,
  formatClock,
  formatDay,
  exchangeLabel,
  exchangeColor,
  pnlSign,
  pctSigned,
  toNum,
} from '@/lib/format'
import { EmptyState } from '@/components/common/EmptyState'
import { Spinner } from '@/components/common/Spinner'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { InfoHint } from '@/components/common/InfoHint'
import { ModeBadge } from '@/components/layout/Indicators'
import { ListIcon, TrashIcon, ArrowUpIcon, ArrowDownIcon } from '@/components/common/icons'
import type { AutobotHook } from './useAutobot'

interface LogHistoryTabsProps {
  run: AutobotHook['run']
  pending: string | null
  /** Меняется на каждое WS-событие — триггерит мягкий рефреш активной вкладки. */
  eventStamp: number
}

type Tab = 'history' | 'log'

const EXIT_REASON: Record<string, string> = {
  manual: 'Вручную',
  funding_scheduled: 'Плановый (фандинг)',
  funding_deterioration: 'Ухудшение фандинга',
  stop_loss: 'Стоп-лосс',
  liquidation_risk: 'Риск ликвидации',
  mark_divergence: 'Расхождение mark',
  contract_delist: 'Делистинг',
  account_margin: 'Маржа счёта',
  daily_loss_limit: 'Дневной лимит',
  emergency: 'Авария',
  entry_aborted: 'Вход не налился',
}

export function LogHistoryTabs({ run, pending, eventStamp }: LogHistoryTabsProps) {
  const [tab, setTab] = useState<Tab>('history')
  const [history, setHistory] = useState<ClosedTrade[] | null>(null)
  const [log, setLog] = useState<string[] | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [confirmClear, setConfirmClear] = useState<Tab | null>(null)
  const mounted = useRef(true)
  // Монотонный id загрузки (анти-clobber) + ref активной вкладки (для рефреша по событию
  // без повторного срабатывания на смене вкладки).
  const loadSeq = useRef(0)
  // Отдельный id владельца спиннера: анти-clobber-seq для снятия спиннера не годится —
  // мягкий рефреш по WS-событию (withSpinner=false) обгоняет загрузку со спиннером, и
  // та в finally видит `seq !== loadSeq` → спиннер не снимался НИКОГДА (панель навсегда
  // на «Загрузка…», т.к. все последующие загрузки идут без спиннера). Спиннер снимает
  // последняя загрузка, которая его показала; данные по-прежнему пишет только свежайшая.
  const spinnerSeq = useRef(0)
  const tabRef = useRef<Tab>(tab)
  tabRef.current = tab

  const handleError = useCallback((err: unknown) => {
    if (err instanceof DOMException && err.name === 'AbortError') return
    if (err instanceof ApiError && err.status === 401) {
      useAppStore.getState().logout()
      return
    }
    if (mounted.current) setError(err instanceof ApiError ? err.detail : 'Не удалось загрузить')
  }, [])

  const load = useCallback(
    async (which: Tab, withSpinner: boolean, signal?: AbortSignal) => {
      const seq = ++loadSeq.current
      const fresh = () => mounted.current && seq === loadSeq.current
      if (withSpinner) {
        spinnerSeq.current = seq
        setLoading(true)
      }
      try {
        if (which === 'history') {
          const resp = await getHistory(200, signal)
          if (fresh()) setHistory(resp.records)
        } else {
          const resp = await getLog(300, signal)
          if (fresh()) setLog(resp.lines)
        }
        if (fresh()) setError(null)
      } catch (err) {
        if (seq === loadSeq.current) handleError(err)
      } finally {
        // Не `fresh()`: спиннер принадлежит своей загрузке, а не свежайшей вообще.
        if (withSpinner && mounted.current && seq === spinnerSeq.current) setLoading(false)
      }
    },
    [handleError],
  )

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  // Загружаем данные активной вкладки (со спиннером при первом показе пустой). При смене
  // вкладки прошлый запрос отменяется (AbortController), чтобы «чужой» ответ не прилетел.
  useEffect(() => {
    const empty = tab === 'history' ? history === null : log === null
    const ctrl = new AbortController()
    void load(tab, empty, ctrl.signal)
    return () => ctrl.abort()
  }, [tab, load]) // eslint-disable-line react-hooks/exhaustive-deps

  // Мягкий рефреш ТЕКУЩЕЙ вкладки на WS-событие (новая закрытая связка / лог); tab — через
  // ref, чтобы эффект не дублировал загрузку при смене вкладки (её делает эффект выше).
  useEffect(() => {
    if (eventStamp > 0) void load(tabRef.current, false)
  }, [eventStamp, load])

  const doClear = useCallback(
    async (which: Tab) => {
      const kind = which === 'history' ? CommandKind.CLEAR_HISTORY : CommandKind.CLEAR_LOG
      const ok = await run(`clear-${which}`, kind)
      if (ok) await load(which, true)
    },
    [run, load],
  )

  const clearBusy = pending === `clear-${tab}`

  return (
    <div className="panel">
      <div className="panel__head">
        <div className="tabs" role="tablist" aria-label="Журнал">
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'history'}
            className={`tab${tab === 'history' ? ' is-active' : ''}`}
            onClick={() => setTab('history')}
          >
            История сделок
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={tab === 'log'}
            className={`tab${tab === 'log' ? ' is-active' : ''}`}
            onClick={() => setTab('log')}
          >
            Лог
          </button>
        </div>
        <button
          type="button"
          className="btn btn--sm"
          disabled={clearBusy}
          onClick={() => setConfirmClear(tab)}
        >
          {clearBusy ? <Spinner size={13} className="btn__spin" /> : <TrashIcon size={14} />}
          Очистить {tab === 'history' ? 'историю' : 'лог'}
        </button>
      </div>

      <div className="panel__pad">
        {loading ? (
          <div className="empty">
            <Spinner size={22} />
            <div className="empty__text">Загрузка…</div>
          </div>
        ) : error ? (
          <div className="note note--warn">{error}</div>
        ) : tab === 'history' ? (
          <HistoryTable records={history ?? []} />
        ) : (
          <LogView lines={log ?? []} />
        )}
      </div>

      <ConfirmDialog
        open={confirmClear !== null}
        danger
        title={confirmClear === 'log' ? 'Очистить лог?' : 'Очистить историю?'}
        message={
          confirmClear === 'log'
            ? 'Лог-файл будет усечён. Это действие необратимо.'
            : 'История закрытых связок будет очищена. Это действие необратимо.'
        }
        confirmLabel="Очистить"
        busy={clearBusy}
        onCancel={() => setConfirmClear(null)}
        onConfirm={() => {
          const which = confirmClear
          setConfirmClear(null)
          if (which) void doClear(which)
        }}
      />
    </div>
  )
}

/** Нога в колонке «Биржи» истории: стрелка направления + цвет + имя биржи. */
function HistLeg({ exchange, dir }: { exchange: string; dir: 'long' | 'short' }) {
  const Arrow = dir === 'long' ? ArrowUpIcon : ArrowDownIcon
  return (
    <span className="leg-ex">
      <span className={`leg-arrow leg-arrow--${dir}`}>
        <Arrow size={13} />
      </span>
      <span className="ex-chip__dot" style={{ background: exchangeColor(exchange) }} aria-hidden="true" />
      <span className="leg-ex__name">{exchangeLabel(exchange)}</span>
    </span>
  )
}

/** % изменения цены выхода относительно входа (для колонки «Выход», §146). */
function priceChangePct(entry: string | null, exit: string | null): string {
  const e = toNum(entry)
  const x = toNum(exit)
  if (e === null || x === null || e === 0) return ''
  return pctSigned(((x - e) / e) * 100)
}

/** Значение в % от «Объёма» (для «Доход с фандинга» и «Итог», §146). */
function pctOfNotional(value: string, notional: string): string {
  const v = toNum(value)
  const n = toNum(notional)
  if (v === null || n === null || n === 0) return ''
  return pctSigned((v / n) * 100)
}

/** «Итог» сделки = realized_pnl + funding_income — та же формула, что «PNL закрытых»
 * в статистике (display-only: деньги считает бэкенд, тут только отображение суммы). */
function totalPnl(realized: string, funding: string): string {
  const r = toNum(realized)
  const f = toNum(funding)
  if (r === null || f === null) return ''
  return String(r + f)
}

function HistoryTable({ records }: { records: ClosedTrade[] }) {
  if (records.length === 0) {
    return (
      <EmptyState
        icon={<ListIcon size={22} />}
        title="История пуста"
        text="Закрытые связки появятся здесь: вход/выход, объём, доход с фандинга и итог сделки."
      />
    )
  }
  return (
    <div className="tbl-wrap">
      <table className="tbl tbl--history">
        <thead>
          <tr>
            <th className="col-lead">Пара</th>
            <th className="col-lead">Биржи</th>
            <th>Вход</th>
            <th>
              Выход
              <InfoHint text="Цена выхода на каждой бирже и процент изменения относительно цены входа той же ноги." />
            </th>
            <th>Объём</th>
            <th>
              Доход с фандинга
              <InfoHint text="Полученный доход с фандинга за сделку ($ и % от объёма). В 14-lite/dry-run быстрый цикл не начисляет фандинг → 0; полная экономика — soak/эт.18/20." />
            </th>
            <th>
              Цена−комиссии
              <InfoHint text="Реализованный результат по ценам входа/выхода за вычетом комиссий, БЕЗ дохода с фандинга (раньше эта колонка называлась «PNL»)." />
            </th>
            <th>
              Итог
              <InfoHint text="Полный результат сделки = доход с фандинга + (цена − комиссии). Сумма по этой колонке сходится с «PNL закрытых» в статистике." />
            </th>
            <th>Причина</th>
            <th>Закрыта</th>
          </tr>
        </thead>
        <tbody>
          {records.map((r) => {
            const total = totalPnl(r.realized_pnl, r.funding_income)
            return (
            <tr key={r.link_id}>
              <td className="col-lead">
                <div className="hist-pair">
                  <span className="coin__name">{r.coin}</span>
                  <ModeBadge mode={r.mode} />
                </div>
              </td>
              <td className="col-lead">
                <div className="leg-stack">
                  <HistLeg exchange={r.long_exchange} dir="long" />
                  <HistLeg exchange={r.short_exchange} dir="short" />
                </div>
              </td>
              <td>
                <div className="leg-stack tnum">
                  <span>{formatPrice(r.long_entry_price)}</span>
                  <span>{formatPrice(r.short_entry_price)}</span>
                </div>
              </td>
              <td>
                <div className="leg-stack tnum">
                  <span className="hist-exit">
                    {formatPrice(r.long_exit_price)}
                    <span className="hist-exit__pct">
                      {priceChangePct(r.long_entry_price, r.long_exit_price)}
                    </span>
                  </span>
                  <span className="hist-exit">
                    {formatPrice(r.short_exit_price)}
                    <span className="hist-exit__pct">
                      {priceChangePct(r.short_entry_price, r.short_exit_price)}
                    </span>
                  </span>
                </div>
              </td>
              <td className="tnum">{formatUsd(r.notional_usd, { digits: 0 })}</td>
              <td className="tnum">
                <div className={`pnl pnl--${pnlSign(r.funding_income)}`}>
                  <span className="pnl__usd">{formatUsd(r.funding_income, { signed: true })}</span>
                  <span className="pnl__pct">{pctOfNotional(r.funding_income, r.notional_usd)}</span>
                </div>
              </td>
              <td className="tnum">
                <div className={`pnl pnl--muted pnl--${pnlSign(r.realized_pnl)}`}>
                  <span className="pnl__usd">{formatUsd(r.realized_pnl, { signed: true })}</span>
                  <span className="pnl__pct">{pctOfNotional(r.realized_pnl, r.notional_usd)}</span>
                </div>
              </td>
              <td className="tnum">
                <div className={`pnl pnl--${pnlSign(total)}`}>
                  <span className="pnl__usd">{formatUsd(total, { signed: true })}</span>
                  <span className="pnl__pct">{pctOfNotional(total, r.notional_usd)}</span>
                </div>
              </td>
              <td>
                <span className="badge badge--neutral">
                  {EXIT_REASON[r.exit_reason] ?? r.exit_reason}
                </span>
              </td>
              <td className="tnum">
                <div className="stamp">
                  <span className="stamp__day">{formatDay(r.closed_ts_ms)}</span>
                  <span>{formatClock(r.closed_ts_ms)}</span>
                </div>
              </td>
            </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function LogView({ lines }: { lines: string[] }) {
  if (lines.length === 0) {
    return (
      <EmptyState
        icon={<ListIcon size={22} />}
        title="Лог пуст"
        text="Строки лога движка появятся здесь (хвост файла)."
      />
    )
  }
  // Сервер отдаёт хвост файла в хронологическом порядке (старые сверху). Показываем
  // в обратном — новые строки сверху, чтобы видеть свежие события без прокрутки вниз.
  return (
    <pre className="log-view" aria-label="Лог движка">
      {lines.slice().reverse().join('\n')}
    </pre>
  )
}
